import { FC } from "react";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineContent,
  TimelineOppositeContent
} from "@mui/lab";
import { Badge } from "@vilocnv/allsetra-core";
import {
  LocationTimelineContainer,
  LocationTimelineWrapper,
  TimelineContentDetailsWrapper,
  TimelineContentWrapper,
  TimelineBox,
  CustomTimelineConnector
} from "./LocationTimeline.styled";
import { EndLocation, StartLocation } from "assets/icons";
import { HiddenBadgeIcon } from "components/ui/icons";
import moment from "moment";
import { getRideAddress, getSplittedAddress } from "app/data/helpers";
import { useTranslation } from "react-i18next";

export const timeLineColors = [
  "#1CA0BD",
  "#4560E4",
  "#3E34C1",
  "#6E44A8",
  "#BC3FB7"
];

export interface TimeLineItemProps {
  resolvedAddress: string;
  date: string;
  latitude: number;
  longitude: number;
  accuracy?: number | null;
  altitude?: number | null;
  startAddress?: string;
  endAddress?: string;
  altitudeAccuracy?: any;
  locationProvider?: {
    locationType: string;
    provider: string;
  };
}

export interface LocationTimelineProps {
  timeLineItems: Array<TimeLineItemProps>;
}

const LocationTimeline: FC<any> = ({ timeLineItems }) => {
  const { t } = useTranslation();

  const renderAddress = (address: string) => {
    const addressIsHidden = t("typography.addressIsHidden");
    const addressUnknown = t("typography.addressUnknown");

    if (address === addressIsHidden) {
      return (
        <Badge
          colorScheme="blank"
          icon={<HiddenBadgeIcon />}
          textWeight="light"
        >
          {addressIsHidden}
        </Badge>
      );
    }

    if (address === addressUnknown) {
      return (
        <Badge colorScheme="blank" textWeight="light">
          {addressUnknown}
        </Badge>
      );
    }

    if (address === "N/A") {
      return (
        <Badge colorScheme="blank" textWeight="light">
          N/A
        </Badge>
      );
    }

    return (
      <>
        {getSplittedAddress(0, address)}
        <br />
        {getSplittedAddress(1, address) + getSplittedAddress(2, address)}
      </>
    );
  };

  return (
    <LocationTimelineContainer>
      {timeLineItems && (
        <Timeline>
          {timeLineItems.map(
            (item: TimeLineItemProps, index: number, array: any) => {
              const address = getRideAddress(index, item, array);

              return (
                <LocationTimelineWrapper
                  key={`${index}`}
                  color={timeLineColors[index % timeLineColors.length]}
                  color2={timeLineColors[index % timeLineColors.length]}
                  height={index + 1 === array.length ? "65px" : "120px"}
                >
                  <TimelineItem>
                    <TimelineOppositeContent
                      sx={{ flex: "0 0 60px", minWidth: "60px" }}
                    >
                      {moment(item?.date).format("HH:mm")}
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineBox>
                        {index === 0 ? <StartLocation /> : <EndLocation />}
                      </TimelineBox>

                      {!(index + 1 === array.length) && (
                        <CustomTimelineConnector />
                      )}
                    </TimelineSeparator>
                    <TimelineContent
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        maxWidth: "none",
                        width: "100%",
                        overflow: "visible"
                      }}
                    >
                      <TimelineContentWrapper>
                        <h3
                          style={{
                            width: "100%",
                            maxWidth: "none",
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal"
                          }}
                        >
                          {renderAddress(address)}
                        </h3>
                        <TimelineContentDetailsWrapper></TimelineContentDetailsWrapper>
                      </TimelineContentWrapper>
                    </TimelineContent>
                  </TimelineItem>
                </LocationTimelineWrapper>
              );
            }
          )}
        </Timeline>
      )}
    </LocationTimelineContainer>
  );
};

export default LocationTimeline;
